import { useCallback, useEffect, useState } from "react";

import {
  CloseXSm,
  Shuffle as ShuffleIcon,
  ArrowRight,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  ErrorTriangle,
  CodeBrowser,
} from "@hl/base-components/lib/assets/icons.generated";
import { Expand01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import {
  BACKGROUND_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { GenArtIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import { useGenParams } from "@hl/shared-features/lib/features/gen-art/gen-art-utils";
import {
  PreviewStatus,
  usePreviewImage,
} from "@hl/shared-features/lib/features/gen-art/preview";
import {
  Button,
  Grid,
  Group,
  NumberInput,
  Select,
  Text,
  Flex,
  ActionIcon,
  createStyles,
  Center,
  Loader,
  Image,
  Stack,
  useMantineTheme,
  Switch,
  TextInput,
  MantineTheme,
} from "@mantine/core";
import { useDebouncedState, usePrevious } from "@mantine/hooks";

import {
  CapturePreviewTriggerType,
  DraftProgress,
} from "~/apollo/graphql.generated";

import FormTitle from "../FormTitle";
import NextStepButton from "../NextStepButton";
import SaveDraftButton from "../SaveDraftButton";
import {
  DEFAULT_PREVIEW_SIZE,
  defaultCaptureSettings,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "../context";
import useSaveDraft from "../useSaveDraft";

const inputStyle = (theme: MantineTheme) => ({
  border: `1px solid ${theme.colors.divider[0]}`,
  borderRadius: "6px",
});

const useStyles = createStyles((theme) => ({
  expandBtn: {
    position: "absolute",
    right: 10,
    bottom: 10,
    zIndex: 10,
    height: 32,
    width: 32,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    boxShadow: theme.shadows.xs,
  },
  expandedTitle: {
    marginTop: 46,
    marginBottom: 26,
    backgroundColor: theme.colors.secondaryBackground[0],
    borderRadius: 999,
    height: 32,
    fontSize: 14,
  },
  preview: {
    border: `0.5px solid ${theme.colors.divider[0]}`,
  },
}));

export const CodeGePreviewImage = () => {
  const {
    dispatch,
    state: { fullWidthMode: isExpanded },
  } = useCreateCollectionState();
  const showGPUSupportToggle = useFeatureFlags(
    FEATURE_FLAGS.SHOW_GPU_SUPPORT_TOGGLE
  );
  const showCaptureSelectorType = useFeatureFlags(
    FEATURE_FLAGS.SHOW_CAPTURE_SELECTOR_TYPE
  );
  const form = useCreateCollectionFormContext();

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_GEN_PREVIEW,
  });
  const { classes } = useStyles();

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };
  const mediaUrl = form.values.metadata.mediaUrl;
  const isCurated = form.values.generativeDetails?.curateOutput ?? false;

  const { params, randomizeAll } = useGenParams({ isCurated });
  const { generativeDetails } = form.values;
  const { viewPort, ...storedSettings } =
    generativeDetails?.captureSettings || defaultCaptureSettings;

  const [trigger, setTrigger] = useState<CapturePreviewTriggerType>(
    storedSettings.trigger
  );
  const [captureType, setCaptureType] = useState<string>(
    storedSettings.selector ? "Selector" : "ViewPort"
  );
  const previousCaptureType = usePrevious(captureType);
  const [selectorType, setSelectorType] = useState<string>(
    storedSettings.selectorType || "ELEMENT"
  );
  const [isGPURendering, setIsGPURendering] = useState<boolean>(
    !!generativeDetails?.isGPURendering
  );
  const [delay, setDelay] = useDebouncedState(storedSettings.delay, 1000);
  const [captureSelector, setCaptureSelector] = useDebouncedState(
    storedSettings.selector,
    1000
  );
  const [width, setWidth] = useDebouncedState(viewPort.width, 1000);
  const [height, setHeight] = useDebouncedState(viewPort.height, 1000);

  const { previewStatus, imageUrl } = usePreviewImage({
    mediaUrl,
    params,
    width,
    height,
    trigger,
    delay,
    selector: captureSelector ?? undefined,
    isGPURendering,
    selectorType,
  });

  const theme = useMantineTheme();

  useEffect(() => {
    form.setFieldValue("generativeDetails", {
      ...form.values.generativeDetails,
      captureSettings: {
        trigger,
        delay,
        viewPort: {
          height,
          width,
        },
        selector: captureSelector ?? undefined,
        selectorType,
      },
      isGPURendering,
    });
  }, [
    width,
    height,
    trigger,
    delay,
    isGPURendering,
    captureSelector,
    selectorType,
  ]);

  useEffect(() => {
    if (previousCaptureType && captureType !== previousCaptureType) {
      setWidth(DEFAULT_PREVIEW_SIZE);
      setHeight(DEFAULT_PREVIEW_SIZE);
      setCaptureSelector("");
    }
  }, [captureType]);

  const expandView = useCallback(() => {
    dispatch({
      type: "SET_FULL_WIDTH",
      payload: { enable: true },
    });
  }, [dispatch]);

  if (!mediaUrl) {
    return <div>error: zip with assets is not uploaded</div>;
  }
  return (
    <div>
      {!isExpanded && (
        <>
          <FormTitle
            title="Preview images"
            buttons={[
              <SaveDraftButton
                key="draft"
                onClick={() => saveDraft()}
                loading={loading}
              />,
              <NextStepButton
                key="next"
                onClick={onNextStep}
                loading={loading}
              />,
            ]}
          />
          <Text size="sm">
            Next, ensure that preview images are being generated correctly.
            Token preview images are static images used on platforms like
            OpenSea to represent a token when the full token live view
            can&rsquo;t be shown. You can choose both when to capture a preview
            image and what to capture. Test different token previews by clicking
            test another token. The image on the right will be used as the
            preview image for the token on the left.
          </Text>

          <Group mt={40} position="apart">
            <Select
              label="Preview image trigger"
              value={trigger}
              onChange={(val) => setTrigger(val as CapturePreviewTriggerType)}
              data={[
                {
                  value: CapturePreviewTriggerType.Delay,
                  label: "Capture preview image after a time delay",
                },
                {
                  value: CapturePreviewTriggerType.Function,
                  label:
                    "Capture preview image by calling $hl.token.capturePreview() in code",
                },
              ]}
              w="65%"
            ></Select>
            <NumberInput
              label="Delay time"
              rightSection={
                <Text color={TEXT_COLOR.SECONDARY} mr={66}>
                  seconds
                </Text>
              }
              w="30%"
              defaultValue={delay}
              onChange={(val) => setDelay(val ?? 3)}
              min={1}
              max={120}
              disabled={trigger == CapturePreviewTriggerType.Function}
            />
          </Group>

          {!showCaptureSelectorType && (
            <Group mt={40} position="apart">
              <Select
                label="Preview image area"
                value={captureType}
                onChange={(val) =>
                  setCaptureType(val as "ViewPort" | "Selector")
                }
                data={[
                  {
                    value: "ViewPort",
                    label: "Capture the entire viewport",
                  },
                  {
                    value: "Selector",
                    label: "Capture a specific canvas element",
                  },
                ]}
                w={captureType === "ViewPort" ? "100%" : "45%"}
              ></Select>
              {captureType === "Selector" && (
                <TextInput
                  label="Canvas element selector"
                  w="50%"
                  placeholder="#defaultCanvas0"
                  defaultValue={captureSelector}
                  onChange={(val) =>
                    setCaptureSelector(val.currentTarget.value)
                  }
                />
              )}
            </Group>
          )}

          {!!showCaptureSelectorType && (
            <>
              <Group mt={40} position="apart">
                <Select
                  label="Preview image area"
                  value={captureType}
                  onChange={(val) =>
                    setCaptureType(val as "ViewPort" | "Selector")
                  }
                  data={[
                    {
                      value: "ViewPort",
                      label: "Capture the entire viewport",
                    },
                    {
                      value: "Selector",
                      label: "Capture a specific canvas element",
                    },
                  ]}
                  w="100%"
                ></Select>
              </Group>
              {captureType === "Selector" && (
                <Group mt={40} position="apart">
                  <>
                    <TextInput
                      label="Canvas element selector"
                      w="45%"
                      placeholder="#defaultCanvas0"
                      defaultValue={captureSelector}
                      onChange={(val) =>
                        setCaptureSelector(val.currentTarget.value)
                      }
                    />
                    <Select
                      label="Selector type"
                      value={selectorType}
                      onChange={(val) =>
                        setSelectorType(val as "ELEMENT" | "SVG")
                      }
                      data={[
                        {
                          value: "ELEMENT",
                          label: "Element",
                        },
                        {
                          value: "SVG",
                          label: "SVG",
                        },
                      ]}
                      w="50%"
                    ></Select>
                  </>
                </Group>
              )}
            </>
          )}

          {captureType === "ViewPort" && (
            <>
              <Text fw={500} mt={40} size="sm">
                Preview image resolution
              </Text>
              <Group position="apart" align="center">
                <NumberInput
                  w="45%"
                  defaultValue={width}
                  onChange={(val) => setWidth(val ?? DEFAULT_PREVIEW_SIZE)}
                  hideControls
                  rightSection={
                    <Text color={TEXT_COLOR.SECONDARY} mr={16}>
                      W
                    </Text>
                  }
                  min={400}
                  max={6000}
                />
                <CloseXSm />
                <NumberInput
                  w="45%"
                  defaultValue={height}
                  onChange={(val) => setHeight(val ?? DEFAULT_PREVIEW_SIZE)}
                  hideControls
                  rightSection={
                    <Text color={TEXT_COLOR.SECONDARY} mr={16}>
                      H
                    </Text>
                  }
                  min={400}
                  max={6000}
                />
              </Group>
            </>
          )}

          {!!showGPUSupportToggle && (
            <>
              <Text fw={500} mt={40}>
                GPU Support
              </Text>
              <Text size="sm" mb={8}>
                Choose whether to enable GPU support when rendering preview
                images. Only enable this option if you’re using WebGL, shaders,
                or other techniques that require a GPU to render properly.
              </Text>
              <Stack sx={inputStyle} spacing={0}>
                <Group
                  px={16}
                  position="apart"
                  align="center"
                  spacing={0}
                  h={48}
                >
                  <Text size="sm">
                    Enable GPU-supported rendering for preview images
                  </Text>
                  <Switch
                    mt={4}
                    mr={4}
                    sx={{
                      input: {
                        height: "100%",
                      },
                    }}
                    {...form.getInputProps("generativeDetails.isGPURendering", {
                      type: "checkbox",
                    })}
                    value={isGPURendering}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setIsGPURendering(checked);
                    }}
                  />
                </Group>
              </Stack>
            </>
          )}
        </>
      )}
      <Grid columns={21} m={0}>
        <Grid.Col span={10} p={0}>
          {isExpanded ? (
            <Center>
              <Group
                className={classes.expandedTitle}
                spacing={8}
                px={12}
                py={6}
              >
                <CodeBrowser width={16} height={16} />
                <span>Live view</span>
              </Group>
            </Center>
          ) : (
            <Text fw={500} mt={40} mb={8} size="sm">
              Live view
            </Text>
          )}
        </Grid.Col>
        <Grid.Col span={1}></Grid.Col>
        <Grid.Col span={10} p={0}>
          {isExpanded ? (
            <Center>
              <Group
                className={classes.expandedTitle}
                spacing={8}
                px={12}
                py={6}
              >
                <ImageIcon />
                <span>Preview image</span>
              </Group>
            </Center>
          ) : (
            <Text fw={500} mt={40} mb={8} size="sm">
              Preview image
            </Text>
          )}
        </Grid.Col>
        {/* next row */}
        <Grid.Col span={10} p={0}>
          <Flex pos="relative" align="center" className={classes.preview}>
            <GenArtIFrame
              mediaUrl={mediaUrl}
              {...params}
              bordered={false}
              aspectRatio={width !== height ? `${width}/${height}` : "1"}
            />
            {!isExpanded && (
              <ActionIcon className={classes.expandBtn} onClick={expandView}>
                <Expand01 width={16} height={16} />
              </ActionIcon>
            )}
          </Flex>
        </Grid.Col>
        <Grid.Col span={1}>
          {!isExpanded && (
            <Flex px={5} justify="center" align="center" h="100%">
              <ArrowRight />
            </Flex>
          )}
        </Grid.Col>
        <Grid.Col span={10} p={0}>
          <Flex
            justify="center"
            align="center"
            pos="relative"
            h="100%"
            className={classes.preview}
          >
            {previewStatus === PreviewStatus.Processing && (
              <Stack spacing={8} align="center" ta="center" p={40}>
                <Loader size="sm" mb={8} />
                <Text size="sm" fw={WEIGHT_BOLD}>
                  Capturing preview image for this token
                </Text>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  This could take a few minutes depending on the delay you have
                  set or when your code reaches hl.token.capturePreview()
                </Text>
              </Stack>
            )}
            {previewStatus === PreviewStatus.Done && (
              <>
                <Image
                  src={imageUrl}
                  fit="contain"
                  width="100%"
                  height="auto"
                />
                {!isExpanded && (
                  <ActionIcon
                    className={classes.expandBtn}
                    onClick={expandView}
                  >
                    <Expand01 width={16} height={16} />
                  </ActionIcon>
                )}
              </>
            )}
            {previewStatus === PreviewStatus.Failed && (
              <Stack spacing={8} align="center" ta="center" p={40}>
                <ErrorTriangle
                  width={24}
                  height={24}
                  color={theme.colors.errorStatus[0]}
                />
                <Text size="sm" fw={WEIGHT_BOLD}>
                  Failed to capture preview image
                </Text>
                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                  The operation timed out without capturing a preview image, or
                  a runtime error occurred. Make sure you’re calling
                  hl.token.capturePreview() in your code.
                </Text>
              </Stack>
            )}
          </Flex>
        </Grid.Col>
      </Grid>
      {isExpanded && (
        <Center mt={86}>
          <Button leftIcon={<ShuffleIcon />} onClick={randomizeAll}>
            Test another token
          </Button>
        </Center>
      )}
      {!isExpanded && (
        <>
          <Button
            color={BACKGROUND_COLOR.SECONDARY}
            leftIcon={<RefreshIcon />}
            fullWidth
            mt={20}
            onClick={randomizeAll}
          >
            Reload with new token
          </Button>

          <NextStepButton
            onClick={onNextStep}
            loading={loading}
            mt={40}
            fullWidth
          />
        </>
      )}
    </div>
  );
};
